import { EventData, STATUS, Step } from 'react-joyride';
import { useCallback, useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { dashboardTourTarget } from '../components/tours';

type DashboardTour = 'header' | 'dashboard-selection' | 'sidebar-widget';

interface ActiveDashboardTour {
  type: DashboardTour;
  widgetId?: string;
}

interface UseDashboardToursResult {
  isDashboardTourRunning: boolean;
  dashboardTourSteps: Step[];
  handleDashboardTourEvent: (data: EventData) => void;
  handleStartHeaderTour: () => void;
  handleStartDashboardSelectionTour: () => void;
  handleStartSidebarWidgetTour: (widgetId: string) => void;
}

const createStep = (
  target: string,
  content: string,
  placement: Step['placement'] = 'bottom'
): Step => ({
  target: dashboardTourTarget(target),
  content,
  placement,
  skipBeacon: true,
});

const headerTourSteps: Step[] = [
  createStep(
    'header-dashboards',
    'Dashboards opens the left selector so you can search and switch between dashboards.',
    'bottom-start'
  ),
  createStep(
    'header-edit-dashboard',
    'Dashboard settings opens the right sidebar for renaming the dashboard and changing its time period.'
  ),
  createStep(
    'header-title',
    'The title shows which dashboard you are viewing. Long names are truncated here to keep the header compact.'
  ),
  createStep(
    'header-period',
    'The time period shows the range used by this dashboard. Click it to edit the dashboard period.'
  ),
  createStep(
    'header-layout',
    'Edit layout lets you move and resize widgets. When editing is active, this becomes Save layout.'
  ),
  createStep(
    'header-add-widget',
    'Add widget opens the right sidebar where you can create a new chart, table, or other dashboard widget.'
  ),
  createStep(
    'header-variables',
    'Variables opens the right sidebar for dashboard variables. Variables can be reused by widgets as configurable values.'
  ),
  createStep(
    'header-toggle-app-header',
    'This hides or shows the main application header to give the dashboard more vertical space.'
  ),
  createStep(
    'dashboard-canvas',
    'This is the dashboard workspace. Widgets appear here; empty dashboards show a prompt to create the first widget.',
    'bottom'
  ),
];

const dashboardSelectionTourSteps: Step[] = [
  createStep(
    'dashboard-selection-search',
    'Search filters dashboards by name without leaving the current page.',
    'right'
  ),
  createStep(
    'dashboard-selection-add',
    'Add Dashboard opens the dashboard creation form in the right sidebar.',
    'right'
  ),
  createStep(
    'dashboard-selection-list',
    'This list contains every matching dashboard. Click a row to open that dashboard.',
    'right'
  ),
  createStep(
    'dashboard-selection-favorite',
    'The star marks a dashboard as a favorite. Favorites are saved to your account and shown near the top of the list.',
    'right'
  ),
  createStep(
    'dashboard-selection-default',
    'The house marks your default dashboard. The default dashboard is saved to your account and always appears at the very top.',
    'right'
  ),
  createStep(
    'dashboard-selection-all',
    'All Dashboards opens the full dashboard management page where you can create, edit, and delete dashboards.',
    'right'
  ),
];

const sidebarWidgetTourSteps: Step[] = [
  createStep('widget-form-title', 'Title is the widget name displayed on the dashboard.', 'left'),
  createStep(
    'widget-form-type',
    'Widget type controls how the data is visualized, such as a line chart, bar chart, pie chart, area chart, or table.',
    'left'
  ),
  createStep(
    'widget-form-data-source',
    'Data Source chooses which source powers the widget. Changing it resets the query settings to match that source type.',
    'left'
  ),
  createStep(
    'query-single-device',
    'Device selects the individual device that the widget should read from. Variable-backed values are marked with the code icon.',
    'left'
  ),
  createStep(
    'query-single-sensor',
    'Sensor selects the sensor index on that device. The selected device controls which sensor options are available.',
    'left'
  ),
  createStep(
    'query-specific-devices',
    'Devices limits the battery widget to selected devices. Leave it empty when the widget should include every device.',
    'left'
  ),
  createStep(
    'query-battery-range',
    'Battery Level limits results to devices inside this percentage range.',
    'left'
  ),
  createStep(
    'query-device-groups',
    'Device Groups selects one or more saved groups. The widget requests readings for the devices in those groups.',
    'left'
  ),
  createStep(
    'query-device-sensor-list',
    'Device Sensors is a list of exact device and sensor pairs. Use it when each row should read a specific sensor from a specific device.',
    'left'
  ),
  createStep(
    'query-device-sensor-device',
    'Device chooses which device this row reads from.',
    'left'
  ),
  createStep(
    'query-device-sensor-sensor',
    'Sensor chooses the sensor index for the selected device in this row.',
    'left'
  ),
  createStep(
    'query-device-sensor-add',
    'Add Device Sensor appends another device and sensor pair to the query.',
    'left'
  ),
  createStep(
    'query-device-type-list',
    'Device Sensor Types reads a sensor type across selected devices instead of selecting a single sensor index per row.',
    'left'
  ),
  createStep(
    'query-device-type-devices',
    'Devices chooses which devices are included for this sensor type row.',
    'left'
  ),
  createStep(
    'query-device-type-sensor-type',
    'Sensor Type identifies the analog or digital sensor type to request from the selected devices.',
    'left'
  ),
  createStep(
    'query-device-type-add',
    'Add Sensor Type appends another group of devices and sensor type criteria.',
    'left'
  ),
  createStep(
    'query-prefer-flow',
    'Data Type chooses whether the query should prefer raw readings or calculated flow values when the source supports both.',
    'left'
  ),
  createStep(
    'query-timespan-mode',
    'Time Period controls whether this widget inherits the dashboard period, uses a preset range, or uses custom dates.',
    'left'
  ),
  createStep(
    'query-timespan-preset',
    'Preset selects a relative period such as today, this month, or the past week.',
    'left'
  ),
  createStep(
    'query-timespan-custom',
    'Custom dates set an explicit start and end date. The end date cannot be before the start date.',
    'left'
  ),
  createStep(
    'widget-form-actions',
    'Cancel closes the form. Create or Save persists the widget configuration.',
    'left'
  ),
  createStep(
    'widget-form-delete',
    'Delete Widget removes the widget from the dashboard after confirmation.',
    'left'
  ),
  createStep(
    'dashboard-form-name',
    'Dashboard Name is the saved display name shown in headers and dashboard lists.',
    'left'
  ),
  createStep(
    'dashboard-form-period',
    'Time Period is the default data range for the dashboard. Widgets can inherit this period or use their own.',
    'left'
  ),
  createStep(
    'dashboard-timespan-mode',
    'Choose whether the dashboard uses a preset relative period or a custom date range.',
    'left'
  ),
  createStep(
    'dashboard-timespan-preset',
    'Preset selects a relative dashboard period such as today, this month, or the past week.',
    'left'
  ),
  createStep(
    'dashboard-timespan-custom',
    'Custom dates set an explicit dashboard start and end date. The end date cannot be before the start date.',
    'left'
  ),
  createStep(
    'variable-list-add',
    'Add Variable creates a new dashboard variable that widgets can use in their queries.',
    'left'
  ),
  createStep(
    'variable-list-row',
    'Each variable row shows the variable label and current value. Click the row to edit it.',
    'left'
  ),
  createStep(
    'variable-list-actions',
    'The row action menu lets you edit or delete a variable.',
    'left'
  ),
  createStep(
    'variable-form-name-label',
    'Name is the internal variable key used by queries. Label is the human-friendly name shown in the UI.',
    'left'
  ),
  createStep(
    'variable-form-type',
    'Single variables hold one value. Multi variables hold several values.',
    'left'
  ),
  createStep(
    'variable-form-source',
    'Data Source can populate allowed variable values from a variable source, or stay empty for manual values.',
    'left'
  ),
  createStep(
    'variable-form-value',
    'Value is the current value for the variable. Multi-value variables accept multiple selected or comma-separated values.',
    'left'
  ),
  createStep(
    'variable-form-actions',
    'Cancel closes the form. Create, Save, or Apply locally stores the variable change.',
    'left'
  ),
];

const tourContentKeys: Record<string, string> = {
  'Dashboards opens the left selector so you can search and switch between dashboards.':
    'dashboard_tour_header_dashboards',
  'Dashboard settings opens the right sidebar for renaming the dashboard and changing its time period.':
    'dashboard_tour_header_settings',
  'The title shows which dashboard you are viewing. Long names are truncated here to keep the header compact.':
    'dashboard_tour_header_title',
  'The time period shows the range used by this dashboard. Click it to edit the dashboard period.':
    'dashboard_tour_header_period',
  'Edit layout lets you move and resize widgets. When editing is active, this becomes Save layout.':
    'dashboard_tour_header_layout',
  'Add widget opens the right sidebar where you can create a new chart, table, or other dashboard widget.':
    'dashboard_tour_header_add_widget',
  'Variables opens the right sidebar for dashboard variables. Variables can be reused by widgets as configurable values.':
    'dashboard_tour_header_variables',
  'This hides or shows the main application header to give the dashboard more vertical space.':
    'dashboard_tour_header_toggle_app_header',
  'This is the dashboard workspace. Widgets appear here; empty dashboards show a prompt to create the first widget.':
    'dashboard_tour_dashboard_canvas',
  'Search filters dashboards by name without leaving the current page.':
    'dashboard_tour_selection_search',
  'Add Dashboard opens the dashboard creation form in the right sidebar.':
    'dashboard_tour_selection_add',
  'This list contains every matching dashboard. Click a row to open that dashboard.':
    'dashboard_tour_selection_list',
  'The star marks a dashboard as a favorite. Favorites are saved to your account and shown near the top of the list.':
    'dashboard_tour_selection_favorite',
  'The house marks your default dashboard. The default dashboard is saved to your account and always appears at the very top.':
    'dashboard_tour_selection_default',
  'All Dashboards opens the full dashboard management page where you can create, edit, and delete dashboards.':
    'dashboard_tour_selection_all',
  'Title is the widget name displayed on the dashboard.': 'dashboard_tour_widget_title',
  'Widget type controls how the data is visualized, such as a line chart, bar chart, pie chart, area chart, or table.':
    'dashboard_tour_widget_type',
  'Data Source chooses which source powers the widget. Changing it resets the query settings to match that source type.':
    'dashboard_tour_widget_data_source',
  'Device selects the individual device that the widget should read from. Variable-backed values are marked with the code icon.':
    'dashboard_tour_query_single_device',
  'Sensor selects the sensor index on that device. The selected device controls which sensor options are available.':
    'dashboard_tour_query_single_sensor',
  'Devices limits the battery widget to selected devices. Leave it empty when the widget should include every device.':
    'dashboard_tour_query_specific_devices',
  'Battery Level limits results to devices inside this percentage range.':
    'dashboard_tour_query_battery_range',
  'Device Groups selects one or more saved groups. The widget requests readings for the devices in those groups.':
    'dashboard_tour_query_device_groups',
  'Device Sensors is a list of exact device and sensor pairs. Use it when each row should read a specific sensor from a specific device.':
    'dashboard_tour_query_device_sensor_list',
  'Device chooses which device this row reads from.': 'dashboard_tour_query_device_sensor_device',
  'Sensor chooses the sensor index for the selected device in this row.':
    'dashboard_tour_query_device_sensor_sensor',
  'Add Device Sensor appends another device and sensor pair to the query.':
    'dashboard_tour_query_device_sensor_add',
  'Device Sensor Types reads a sensor type across selected devices instead of selecting a single sensor index per row.':
    'dashboard_tour_query_device_type_list',
  'Devices chooses which devices are included for this sensor type row.':
    'dashboard_tour_query_device_type_devices',
  'Sensor Type identifies the analog or digital sensor type to request from the selected devices.':
    'dashboard_tour_query_device_type_sensor_type',
  'Add Sensor Type appends another group of devices and sensor type criteria.':
    'dashboard_tour_query_device_type_add',
  'Data Type chooses whether the query should prefer raw readings or calculated flow values when the source supports both.':
    'dashboard_tour_query_prefer_flow',
  'Time Period controls whether this widget inherits the dashboard period, uses a preset range, or uses custom dates.':
    'dashboard_tour_query_timespan_mode',
  'Preset selects a relative period such as today, this month, or the past week.':
    'dashboard_tour_query_timespan_preset',
  'Custom dates set an explicit start and end date. The end date cannot be before the start date.':
    'dashboard_tour_query_timespan_custom',
  'Cancel closes the form. Create or Save persists the widget configuration.':
    'dashboard_tour_widget_actions',
  'Delete Widget removes the widget from the dashboard after confirmation.':
    'dashboard_tour_widget_delete',
  'Dashboard Name is the saved display name shown in headers and dashboard lists.':
    'dashboard_tour_dashboard_name',
  'Time Period is the default data range for the dashboard. Widgets can inherit this period or use their own.':
    'dashboard_tour_dashboard_period',
  'Choose whether the dashboard uses a preset relative period or a custom date range.':
    'dashboard_tour_dashboard_timespan_mode',
  'Preset selects a relative dashboard period such as today, this month, or the past week.':
    'dashboard_tour_dashboard_timespan_preset',
  'Custom dates set an explicit dashboard start and end date. The end date cannot be before the start date.':
    'dashboard_tour_dashboard_timespan_custom',
  'Add Variable creates a new dashboard variable that widgets can use in their queries.':
    'dashboard_tour_variable_add',
  'Each variable row shows the variable label and current value. Click the row to edit it.':
    'dashboard_tour_variable_row',
  'The row action menu lets you edit or delete a variable.': 'dashboard_tour_variable_actions',
  'Name is the internal variable key used by queries. Label is the human-friendly name shown in the UI.':
    'dashboard_tour_variable_name_label',
  'Single variables hold one value. Multi variables hold several values.':
    'dashboard_tour_variable_type',
  'Data Source can populate allowed variable values from a variable source, or stay empty for manual values.':
    'dashboard_tour_variable_source',
  'Value is the current value for the variable. Multi-value variables accept multiple selected or comma-separated values.':
    'dashboard_tour_variable_value',
  'Cancel closes the form. Create, Save, or Apply locally stores the variable change.':
    'dashboard_tour_variable_actions_form',
};

const localizeTourSteps = (steps: Step[], t: TFunction): Step[] =>
  steps.map((step: Step): Step => {
    const content = typeof step.content === 'string' ? step.content : '';
    const key = tourContentKeys[content];

    return key ? { ...step, content: t(key, content) } : step;
  });

const isBrowser = (): boolean => typeof document !== 'undefined';

const targetExists = (step: Step): boolean => {
  if (!isBrowser() || typeof step.target !== 'string') {
    return true;
  }

  return !!document.querySelector(step.target);
};

const scopeSidebarWidgetStep = (step: Step, widgetId: string): Step => {
  if (typeof step.target !== 'string') {
    return step;
  }

  return {
    ...step,
    target: `[data-dashboard-tour-widget="${widgetId}"] ${step.target}`,
  };
};

const getTourSteps = (
  activeTour: ActiveDashboardTour,
  localizedTourStepsByTour: Record<DashboardTour, Step[]>
): Step[] => {
  const steps = localizedTourStepsByTour[activeTour.type];

  if (activeTour.type !== 'sidebar-widget' || !activeTour.widgetId) {
    return steps;
  }

  return steps.map((step: Step): Step => scopeSidebarWidgetStep(step, activeTour.widgetId!));
};

const shouldFallbackToUnfilteredSteps = (activeTour: ActiveDashboardTour): boolean => {
  return activeTour.type !== 'sidebar-widget';
};

export const useDashboardTours = (): UseDashboardToursResult => {
  const { t } = useTranslation();
  const [activeTour, setActiveTour] = useState<ActiveDashboardTour | null>(null);

  const localizedTourStepsByTour = useMemo<Record<DashboardTour, Step[]>>(
    () => ({
      header: localizeTourSteps(headerTourSteps, t),
      'dashboard-selection': localizeTourSteps(dashboardSelectionTourSteps, t),
      'sidebar-widget': localizeTourSteps(sidebarWidgetTourSteps, t),
    }),
    [t]
  );

  const dashboardTourSteps = useMemo<Step[]>(() => {
    if (!activeTour) {
      return [];
    }

    const tourSteps = getTourSteps(activeTour, localizedTourStepsByTour);
    const visibleSteps = tourSteps.filter(targetExists);

    if (visibleSteps.length > 0) {
      return visibleSteps;
    }

    return shouldFallbackToUnfilteredSteps(activeTour) ? tourSteps : [];
  }, [activeTour, localizedTourStepsByTour]);

  const handleDashboardTourEvent = useCallback((data: EventData): void => {
    if (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED) {
      setActiveTour(null);
    }
  }, []);

  const handleStartHeaderTour = useCallback((): void => setActiveTour({ type: 'header' }), []);

  const handleStartDashboardSelectionTour = useCallback(
    (): void => setActiveTour({ type: 'dashboard-selection' }),
    []
  );

  const handleStartSidebarWidgetTour = useCallback(
    (widgetId: string): void => setActiveTour({ type: 'sidebar-widget', widgetId }),
    []
  );

  return {
    isDashboardTourRunning: !!activeTour && dashboardTourSteps.length > 0,
    dashboardTourSteps,
    handleDashboardTourEvent,
    handleStartHeaderTour,
    handleStartDashboardSelectionTour,
    handleStartSidebarWidgetTour,
  };
};
